/**
 * Test links start a fresh SPA session: the SPA forwards every query param to whatif-server's
 * session start and only re-runs it when `forceReload` is set, so that one is always present.
 * Param names are whatif-server's (`LandingPageService.HTTP_LANDING_PAGE_ID` = `lpid`, etc.).
 */
export type EntityParamKey = 'lpid' | 'lpgid' | 'sequence_id' | 'offer_id';

export const ENTITY_PARAM_ORDER: EntityParamKey[] = ['lpid', 'lpgid', 'sequence_id', 'offer_id'];

export interface CustomParam {
  key: string;
  value: string;
}

export interface TestLinkInput {
  domain: string | null;
  path: string;
  siteId: number | null;
  /** A key that is present but null is a row the user added without picking anything yet. */
  entities: Partial<Record<EntityParamKey, number | null>>;
  custom: CustomParam[];
}

export interface LinkPart {
  key: string;
  value: string;
  kind: 'param' | 'fixed';
}

export interface TestLink {
  url: string;
  origin: string;
  path: string;
  parts: LinkPart[];
  /** Indexes into `custom` dropped because their key was already in the link. */
  ignoredCustom: number[];
}

/** Commas and @ stay readable (`cash=10,000`, `email=a@b.c`); the servers decode them either way. */
export const encodeParam = (s: string): string =>
  encodeURIComponent(s).replace(/%2C/gi, ',').replace(/%40/g, '@');

/** One leading slash, no query or fragment — those belong in params, not the path. */
export const normalizePath = (raw: string): string => {
  const path = raw.trim().replace(/[?#].*$/, '');
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
};

export function buildTestLink(input: TestLinkInput): TestLink | null {
  const domain = (input.domain ?? '').trim().toLowerCase();
  if (!domain) return null;

  const parts: LinkPart[] = [];
  if (input.siteId != null) parts.push({ key: 'site_id', value: String(input.siteId), kind: 'param' });
  parts.push({ key: 'forceReload', value: 'true', kind: 'fixed' });
  ENTITY_PARAM_ORDER.forEach((key) => {
    const id = input.entities[key];
    if (id != null) parts.push({ key, value: String(id), kind: 'param' });
  });

  // whatif-server reads params case-insensitively, so `LPID` would collide with `lpid`.
  const taken = new Set(parts.map((part) => part.key.toLowerCase()));
  const ignoredCustom: number[] = [];
  input.custom.forEach(({ key, value }, index) => {
    const name = key.trim();
    if (!name) return;
    if (taken.has(name.toLowerCase())) {
      ignoredCustom.push(index);
      return;
    }
    taken.add(name.toLowerCase());
    parts.push({ key: name, value, kind: 'param' });
  });

  const origin = `https://${domain}`;
  const path = normalizePath(input.path);
  const query = parts.map((part) => `${encodeParam(part.key)}=${encodeParam(part.value)}`).join('&');
  return { url: `${origin}${path}?${query}`, origin, path, parts, ignoredCustom };
}
